/**
 * Rewards / Free Shipping Progress Bar — client-side updater.
 *
 * - The drawer and cart-page instances re-render server-side on every cart change;
 *   this script keeps every instance (including the sticky Add-to-Cart bar, which
 *   never re-renders) in sync via the theme's `cart:updated` event, the pubsub
 *   cartUpdate channel, and bfcache restores.
 *
 * - PREVIEW: on the sticky Add-to-Cart bar, when the shopper changes the quantity
 *   or the variant BEFORE adding, the sticky bar previews where their order would
 *   land = current cart total + (selected qty x selected variant price). The other
 *   instances are untouched, and the preview resets to the real cart total on the
 *   next actual cart update (e.g. after Add to cart).
 */
(function () {
  if (window.__fsbInitialized) return;
  window.__fsbInitialized = true;

  // Monotonic sequence so a slow /cart.js response can't overwrite a newer update.
  var seq = 0;
  // Authoritative current cart total (cents); null until seeded.
  var cartTotalCents = null;

  function formatAmount(cents) {
    if (
      typeof window.formatMoney === 'function' &&
      window.MinimogSettings &&
      window.MinimogSettings.money_format
    ) {
      return window.formatMoney(cents, window.MinimogSettings.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function renderBar(bar, totalCents) {
    var threshold = parseInt(bar.dataset.threshold, 10);
    if (!threshold || threshold <= 0) return;

    var remaining = Math.max(threshold - totalCents, 0);
    var percent = Math.min((totalCents / threshold) * 100, 100);

    var fill = bar.querySelector('[data-fsb-fill]');
    if (fill) fill.style.width = percent + '%';

    var amount = bar.querySelector('[data-fsb-remaining]');
    if (amount) amount.innerHTML = formatAmount(remaining);

    bar.classList.toggle('m-fsb--started', totalCents > 0);
    bar.classList.toggle('m-fsb--unlocked', totalCents >= threshold);
  }

  // Render EVERY instance from the real cart total (also clears any sticky preview).
  function renderAll(totalCents) {
    cartTotalCents = totalCents;
    document.querySelectorAll('[data-free-shipping-bar]').forEach(function (bar) {
      renderBar(bar, totalCents);
    });
  }

  function currentCartTotal() {
    if (cartTotalCents !== null) return cartTotalCents;
    var seed = document.querySelector('[data-free-shipping-bar][data-cart-total]');
    cartTotalCents = seed ? parseInt(seed.dataset.cartTotal, 10) || 0 : 0;
    return cartTotalCents;
  }

  // ---- Sticky Add-to-Cart preview ----
  function setupStickyPreview() {
    document.querySelectorAll('.m-sticky-addtocart').forEach(function (sticky) {
      var bar = sticky.querySelector('[data-free-shipping-bar]');
      if (!bar) return;

      var variantSelect = sticky.querySelector('select[name="id"]');

      var priceById = {};
      try {
        var blob = sticky.querySelector('script[type="application/json"]');
        if (blob) {
          (JSON.parse(blob.textContent) || []).forEach(function (v) {
            if (v && v.id != null && typeof v.price === 'number') {
              priceById[String(v.id)] = v.price;
            }
          });
        }
      } catch (e) {
        /* no variant data -> preview simply won't fire */
      }

      function selectedPriceCents() {
        var id = variantSelect ? String(variantSelect.value) : '';
        if (id && priceById[id] != null) return priceById[id];
        var keys = Object.keys(priceById);
        return keys.length ? priceById[keys[0]] : 0;
      }

      function selectedQty() {
        var input = sticky.querySelector('input[name="quantity"]');
        var q = input ? parseInt(input.value, 10) : 1;
        return !q || q < 1 ? 1 : q;
      }

      function preview() {
        var price = selectedPriceCents();
        if (!price) return; // nothing to preview without a price
        renderBar(bar, currentCartTotal() + selectedQty() * price);
      }

      // Quantity typed or variant chosen.
      sticky.addEventListener('change', function (e) {
        var t = e.target;
        if (t === variantSelect || (t && t.name === 'quantity')) preview();
      });
      sticky.addEventListener('input', function (e) {
        if (e.target && e.target.name === 'quantity') preview();
      });
      // The +/- stepper buttons may set the value without firing change/input.
      sticky.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.m-quantity__button')) {
          setTimeout(preview, 0);
        }
      });

      // The MAIN product-page pack selector syncs the sticky <select> value WITHOUT
      // firing a change on it, so the listeners above never see it. The theme
      // dispatches 'variant:changed' on document (after syncing) carrying the chosen
      // variant — drive the preview from that, reading the price straight off the
      // event so it's correct even if the sticky select hasn't synced yet.
      document.addEventListener('variant:changed', function (e) {
        var v = e && e.detail && e.detail.variant;
        var price = v && typeof v.price === 'number' ? v.price : selectedPriceCents();
        if (price) renderBar(bar, currentCartTotal() + selectedQty() * price);
      });

      // The page can open with a pack already selected (the 3 Pack is the store default), and no
      // change event fires for that. Preview it once on load so the sticky bar doesn't say
      // "Only $20.00 more" for a pack that already ships free.
      preview();
    });
  }

  function refreshFromCartApi() {
    var mySeq = ++seq;
    var routes = (window.MinimogSettings && window.MinimogSettings.routes) || {};
    var cartUrl = (routes.cart || '/cart') + '.js';
    fetch(cartUrl)
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (mySeq !== seq) return; // a newer update already rendered
        if (cart && typeof cart.total_price === 'number') renderAll(cart.total_price);
      })
      .catch(function () {});
  }

  function onCartEvent(cart) {
    if (cart && typeof cart.total_price === 'number') {
      seq += 1;
      renderAll(cart.total_price); // real cart total clears any sticky preview
    } else {
      // e.g. /cart/add.js responses don't include cart totals — fetch them.
      refreshFromCartApi();
    }
  }

  document.addEventListener('cart:updated', function (event) {
    onCartEvent(event.detail && event.detail.cart);
  });

  // Fallback channel: fires even when the cart drawer / cart page (cart.js) isn't
  // on the page. Rendering is idempotent, so overlap is harmless.
  if (
    window.MinimogEvents &&
    typeof window.MinimogEvents.subscribe === 'function' &&
    window.MinimogTheme &&
    window.MinimogTheme.pubSubEvents &&
    window.MinimogTheme.pubSubEvents.cartUpdate
  ) {
    window.MinimogEvents.subscribe(window.MinimogTheme.pubSubEvents.cartUpdate, function (event) {
      onCartEvent(event && event.cart);
    });
  }

  // Back/forward-cache restores show the page without re-running cart.js section
  // rendering — re-sync from the authoritative cart state.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshFromCartApi();
  });

  // Seed the cart total from the server-rendered markup and wire up the sticky preview.
  currentCartTotal();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupStickyPreview);
  } else {
    setupStickyPreview();
  }
})();
